import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { authApi } from '../data/auth.api';
import type { AuthState, User, GmailConnection } from '@shared/web/types';

interface AuthContextValue {
  user: User | null;
  gmail: GmailConnection;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: () => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const EMPTY: AuthState = { user: null, gmail: { connected: false } };

/**
 * Single source of truth for "who is signed in".
 *
 * Session state lives on the server (httpOnly cookie), so the client just asks
 * once on mount and caches the answer.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(EMPTY);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setState(await authApi.me());
    } catch {
      setState(EMPTY);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setState(EMPTY);
      window.location.href = '/';
    }
  }, []);

  const value = useMemo(
    () => ({
      user: state.user,
      gmail: state.gmail,
      isLoading,
      isAuthenticated: Boolean(state.user),
      signIn: authApi.startGoogleLogin,
      signOut,
      refresh,
    }),
    [state, isLoading, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
